import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Loader2, Lock, CheckSquare, Square } from "lucide-react";
import { api } from "@/lib/api";
import { decryptJson } from "@/lib/secureCrypto";
import { sanitizeHtml, markdownToHtml } from "@/lib/richtext";
import { PublicShell } from "@/components/Brand";

export default function SecretNote() {
  const { token } = useParams();
  const [sealed, setSealed] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  useEffect(() => {
    api.get(`/public/secret-notes/${token}`).then((r) => setSealed(r.data)).catch((e) => {
      setError(e?.response?.status === 410 ? "This encrypted note has expired." : "This encrypted note doesn't exist.");
    });
  }, [token]);

  const unlock = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try { setNote(await decryptJson(sealed, password.trim())); }
    catch { setError("Wrong password — check with the sender."); }
    finally { setBusy(false); }
  };

  return (
    <PublicShell testId="secret-note-page">
      {note ? (
        <article data-testid="secret-note-content" className="space-y-6">
          <p className="inline-flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-300"><ShieldCheck className="h-4 w-4" />Decrypted on this device</p>
          {note.title && <h1 className="text-4xl sm:text-5xl font-semibold font-display tracking-tight">{note.title}</h1>}
          {note.is_checklist ? (
            <ul className="space-y-2">
              {note.checklist_items.map((i) => (
                <li key={i.id} className="flex items-center gap-2">{i.checked ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}<span className={i.checked ? "line-through text-muted-foreground" : ""}>{i.text}</span></li>
              ))}
            </ul>
          ) : (
            <div className="richtext-view text-base leading-relaxed break-words" dangerouslySetInnerHTML={{ __html: sanitizeHtml(markdownToHtml(note.content)) }} />
          )}
        </article>
      ) : (
        <div className="max-w-sm">
          <div className="h-12 w-12 rounded-2xl bg-emerald-100 dark:bg-emerald-950 flex items-center justify-center mb-6"><Lock className="h-6 w-6 text-emerald-700 dark:text-emerald-300" /></div>
          <h1 className="text-4xl font-semibold font-display tracking-tight">Encrypted note</h1>
          <p className="mt-3 text-sm text-muted-foreground">Enter the password the sender gave you. It never leaves this device.</p>
          {sealed && (
            <form onSubmit={unlock} className="mt-8 space-y-3">
              <Input autoFocus type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" data-testid="secret-password-input" />
              <Button type="submit" disabled={busy || !password.trim()} className="w-full rounded-full h-11" data-testid="secret-unlock-btn">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}Unlock
              </Button>
              <p className="text-xs text-muted-foreground" data-testid="secret-expiry">Available until {new Date(sealed.expires_at).toLocaleDateString()}</p>
            </form>
          )}
          {error && <p className="mt-4 text-sm text-rose-600" data-testid="secret-error">{error}</p>}
        </div>
      )}
    </PublicShell>
  );
}
